// Right-pointing chevron that rotates 0°→90° on expand to match the
// left-nav pattern (`LeftNavV2/NavItemV2.tsx`). Keeping the same icon
// + rotation across the app keeps "collapsed = chevron pointing where
// content will appear" consistent everywhere expand affordances live.
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FeatureFlagKey,
  FeatureFlagMetadata,
  FeatureFlagType,
  KNOWN_FEATURE_FLAG_KEYS,
} from "encamp-shared/src/featureFlags";
import { type GetFlagMeta, useFeatureFlagMeta } from "featureFlags/useFeatureFlagMeta";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import EncampLogo from "../../assets/EncampLogo.svg";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";

export type FlagState = boolean | "mixed";

/**
 * The value a flag will be set to when the pending changes are committed.
 * Undefined means "leave alone" — for multi-tenant bulk edits, each tenant
 * keeps its current value.
 */
export type PendingValue = boolean | undefined;

export type FeatureFlagTogglePanelProps = {
  /**
   * Both modes render a binary `Switch` per flag (visually consistent
   * with the single-tenant page). They differ in how `onChange` fires:
   *  - "single" treats every toggle as an immediate commit and calls
   *    `onChange(key, boolean)`.
   *  - "multi" buffers pending changes per-flag and the caller decides
   *    when to apply them. The third "leave alone" state is preserved
   *    by surfacing a per-row Undo link and a "Mixed across selected
   *    tenants" caption — never by a tri-state control.
   */
  mode: "single" | "multi";
  /**
   * Current state per flag. In single mode values are always boolean; in
   * multi mode "mixed" is valid (meaning selected tenants have a mix of
   * true/false values).
   */
  currentStates: Partial<Record<FeatureFlagKey, FlagState>>;
  /**
   * Staged changes, keyed by flag. Only keys the user has explicitly
   * interacted with should appear here. Used in multi mode.
   */
  pendingChanges?: Partial<Record<FeatureFlagKey, PendingValue>>;
  /**
   * Fires when the user toggles a flag. In single mode `nextValue` is
   * always boolean. In multi mode `undefined` signals the user has
   * reverted the flag to its original ("mixed" or unchanged) state
   * (raised by the per-row Undo link).
   */
  onChange: (key: FeatureFlagKey, nextValue: PendingValue) => void;
  disabled?: boolean;
};

type FlagNode = {
  key: FeatureFlagKey;
  children: FlagNode[];
};

// -----------------------------------------------------------------------
// Expand / collapse persistence
// -----------------------------------------------------------------------
// A single localStorage entry holds expand/collapse state for every flag
// that has children, keyed by `FeatureFlagKey`. Every instance of the
// panel reads from and writes to the same key, so a user who collapses
// "Tier II module" on the single-tenant tab will still see it collapsed
// the next time they open the staff bulk drawer, and vice versa.
//
// We route through `useLocalStorageState` (which wraps
// `use-local-storage-state`) so the parsed value is cached once globally
// per key — N FlagRow instances share a single read + a single
// subscription, instead of each one parsing the same JSON blob in its
// own `useState` initializer (twice in StrictMode).
const EXPANDED_STORAGE_KEY = "featureFlagPanel:expanded";
const EMPTY_EXPANDED_MAP: Record<string, boolean> = {};

function useFlagExpanded(flagKey: FeatureFlagKey): [boolean, () => void] {
  const [expandedMap, setExpandedMap] = useLocalStorageState<Record<string, boolean>>(
    EXPANDED_STORAGE_KEY,
    { defaultValue: EMPTY_EXPANDED_MAP }
  );

  // Default to expanded for any flag we haven't recorded a preference
  // for — the panel was useless when collapsed by default in user
  // testing, so we lean toward "show me everything".
  const expanded = expandedMap[flagKey] ?? true;

  const toggle = useCallback(() => {
    setExpandedMap((prev) => ({
      ...prev,
      [flagKey]: !(prev[flagKey] ?? true),
    }));
  }, [flagKey, setExpandedMap]);

  return [expanded, toggle];
}

// -----------------------------------------------------------------------
// Dependency cascade
// -----------------------------------------------------------------------
/**
 * Given a flag the user is turning on, return the additional flag keys
 * that must also be turned on so the newly-enabled flag actually
 * functions (parents in the hierarchy plus anything in its `requires`
 * list, transitively). Keys that `isEffectivelyOn` already reports as
 * on are excluded.
 *
 * Dependencies with `staffOverride: true` are also excluded: those flags
 * are always-on for staff regardless of stored value, so cascading them
 * on would silently flip the customer-facing flag too — usually not what
 * the staff user is trying to do when they toggle a child or required
 * flag for dogfooding. The {@link DependencyHint} component surfaces the
 * resulting "active for staff only — customer needs X" state instead.
 *
 * Intentionally one-directional on activation. Deactivation cascades
 * children + dependents off (see {@link collectDependencyKeysToDeactivate})
 * — but we only run that flow when the user explicitly toggles a flag
 * off, so they always see the cascade happen.
 */
export function collectDependencyKeysToActivate(
  startKey: FeatureFlagKey,
  isEffectivelyOn: (key: FeatureFlagKey) => boolean,
  getMeta: GetFlagMeta
): FeatureFlagKey[] {
  const queue: FeatureFlagKey[] = [];
  const visited = new Set<FeatureFlagKey>([startKey]);
  const result: FeatureFlagKey[] = [];

  const pushDeps = (key: FeatureFlagKey) => {
    const m = getMeta(key);
    if (m.parentKey) queue.push(m.parentKey);
    for (const r of m.requires) queue.push(r);
  };

  pushDeps(startKey);

  while (queue.length > 0) {
    const k = queue.shift();
    // The `length > 0` guard above means shift() can't actually return
    // undefined here, but the typing reflects the general signature.
    // Falling through with `break` is the typed-safe equivalent of `!`
    // and avoids the AGENTS.md non-null-assertion rule.
    if (k === undefined) break;
    if (visited.has(k)) continue;
    visited.add(k);
    if (isEffectivelyOn(k) || getMeta(k).staffOverride) continue;
    result.push(k);
    pushDeps(k);
  }

  return result;
}

/**
 * Inverse of {@link collectDependencyKeysToActivate}: given a flag the
 * user is turning off, return the additional flag keys that must also
 * be turned off so we never leave a dependent flag in the "inactive —
 * needs …" state.
 *
 * Two classes of dependents are cascaded off:
 *   1. Child flags (any flag whose `parentKey` is reachable from
 *      `startKey`) — turning off a module implies its add-ons also turn
 *      off.
 *   2. Flags that list `startKey` in their `requires` — turning off a
 *      required flag implies any flag that needs it also turns off.
 *
 * Traversal is transitive on both edges, so turning off "Waste module"
 * also turns off "Waste manifest compliance" (which requires
 * "Waste manifest discrepancies", which is a child of Waste module).
 * Keys that `isEffectivelyOn` already reports as off are excluded.
 *
 * If `startKey` itself has `staffOverride: true`, no cascade runs: the
 * flag remains effectively-on for staff after the user toggles it off
 * (it's the customer-facing stored value that flipped, not the staff
 * view), so dependents are still functional for staff and shouldn't be
 * dragged off. The customer-facing inconsistency is surfaced through the
 * {@link DependencyHint} "active for staff only" chip on the dependents.
 */
export function collectDependencyKeysToDeactivate(
  startKey: FeatureFlagKey,
  isEffectivelyOn: (key: FeatureFlagKey) => boolean,
  getMeta: GetFlagMeta
): FeatureFlagKey[] {
  if (getMeta(startKey).staffOverride) return [];

  const queue: FeatureFlagKey[] = [];
  const visited = new Set<FeatureFlagKey>([startKey]);
  const result: FeatureFlagKey[] = [];

  const pushDependents = (key: FeatureFlagKey) => {
    for (const otherKey of KNOWN_FEATURE_FLAG_KEYS) {
      const meta = getMeta(otherKey);
      if (meta.parentKey === key) {
        queue.push(otherKey);
        continue;
      }
      if (meta.requires.includes(key)) {
        queue.push(otherKey);
      }
    }
  };

  pushDependents(startKey);

  while (queue.length > 0) {
    const k = queue.shift();
    // See comment in `collectDependencyKeysToActivate` — guarded read
    // instead of `!` to satisfy the no-non-null-assertion rule.
    if (k === undefined) break;
    if (visited.has(k)) continue;
    visited.add(k);
    if (!isEffectivelyOn(k)) continue;
    result.push(k);
    pushDependents(k);
  }

  return result;
}

function buildFlagTree(getMeta: GetFlagMeta): {
  moduleFlags: FlagNode[];
  otherFlags: FlagNode[];
} {
  // Alphabetical by the human label, locale-aware so e.g. "Émissions" sorts
  // next to "Emissions". Using the label (not the key) so the visible order
  // matches what users scan for.
  const byLabel = (a: FlagNode, b: FlagNode) =>
    getMeta(a.key).label.localeCompare(getMeta(b.key).label, undefined, {
      sensitivity: "base",
    });

  const childrenByParent = new Map<FeatureFlagKey, FlagNode[]>();
  const moduleFlags: FlagNode[] = [];
  const otherFlags: FlagNode[] = [];

  for (const key of KNOWN_FEATURE_FLAG_KEYS) {
    const meta = getMeta(key);
    const node: FlagNode = { key, children: [] };
    if (meta.parentKey) {
      const siblings = childrenByParent.get(meta.parentKey) ?? [];
      siblings.push(node);
      childrenByParent.set(meta.parentKey, siblings);
      continue;
    }
    if (meta.type === FeatureFlagType.Module) {
      moduleFlags.push(node);
    } else {
      // Everything top-level that isn't a Module — type `Other` plus any
      // defensive AddOn entry that's missing a parent (shouldn't happen,
      // but we'd rather still render it than drop it silently).
      otherFlags.push(node);
    }
  }

  for (const module of moduleFlags) {
    module.children = (childrenByParent.get(module.key) ?? []).slice().sort(byLabel);
  }

  moduleFlags.sort(byLabel);
  otherFlags.sort(byLabel);

  return { moduleFlags, otherFlags };
}

export function FeatureFlagTogglePanel(props: FeatureFlagTogglePanelProps) {
  const { getMeta } = useFeatureFlagMeta();
  const { moduleFlags, otherFlags } = useMemo(() => buildFlagTree(getMeta), [getMeta]);

  return (
    <Stack spacing={3}>
      {moduleFlags.length > 0 && (
        <FlagSection title="Product modules">
          {moduleFlags.map((node) => (
            <FlagRow key={node.key} node={node} {...props} />
          ))}
        </FlagSection>
      )}
      {otherFlags.length > 0 && (
        <FlagSection title="Other">
          {otherFlags.map((node) => (
            <FlagRow key={node.key} node={node} {...props} />
          ))}
        </FlagSection>
      )}
    </Stack>
  );
}

function FlagSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {/* Generous spacing between top-level module rows — the sub-flag
          tree rail, chips, and description can make each row tall, and
          tighter spacing runs them together visually. */}
      <Stack spacing={3}>{children}</Stack>
    </Box>
  );
}

type FlagRowProps = FeatureFlagTogglePanelProps & { node: FlagNode; depth?: number };

// The parent module's toggle (a medium MUI Switch in both single and
// multi mode now that multi mode also renders a Switch) is the visual
// anchor for the children rail. Every child of that parent is indented
// by this amount (plus a small gap before the sub-flag's own
// controls), so the child content sits well to the right of the
// parent toggle. The vertical tree line itself is drawn once on the
// parent's *children container* (not per child row), so the line is
// continuous across the gaps between siblings instead of showing as
// disconnected segments.
const PARENT_TOGGLE_WIDTH_PX = 58;
// Default horizontal position of the 2px spine within the children
// container. Chosen so the line's center is directly under the
// parent toggle's center (x=29 = PARENT_TOGGLE_WIDTH_PX / 2). The
// `useToggleColumnCenter` hook below still re-measures defensively so
// the rail stays centered if MUI's Switch width changes.
const CHILDREN_RAIL_LEFT_PX = PARENT_TOGGLE_WIDTH_PX / 2 - 1;
const RAIL_WIDTH_PX = 2;

/**
 * Tracks the rendered width of the parent's toggle column and returns
 * the x-coordinate (in the children container's coordinate system) at
 * which a 2px-wide vertical rail should be drawn so it sits exactly
 * under the toggle's horizontal center.
 *
 * Until the first measurement completes (e.g. on the very first render
 * before layout, or in jsdom where `offsetWidth` returns 0), we fall
 * back to `CHILDREN_RAIL_LEFT_PX` so single-mode rendering is correct
 * out of the gate without needing a layout effect.
 */
function useToggleColumnCenter(enabled: boolean) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [leftPx, setLeftPx] = useState<number>(CHILDREN_RAIL_LEFT_PX);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const w = el.offsetWidth;
      // `offsetWidth` is 0 in jsdom and during the very first paint
      // for some components — keep the default in that case rather
      // than collapse the rail to x=-1.
      if (w <= 0) return;
      setLeftPx(Math.max(0, Math.round(w / 2 - RAIL_WIDTH_PX / 2)));
    };
    update();
    // ResizeObserver is missing in jsdom (test env) and older browsers.
    // Skip the observer when unavailable — the one-shot measurement
    // above is enough for static layouts, and tests don't assert on
    // the rail position anyway.
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [enabled]);

  return { ref, leftPx };
}

function FlagRow({ node, depth = 0, ...panel }: FlagRowProps) {
  const { getMeta } = useFeatureFlagMeta();
  const meta = getMeta(node.key);
  const hasChildren = node.children.length > 0;
  const [expanded, toggleExpanded] = useFlagExpanded(node.key);
  // Keep the children rail centered under the parent's toggle column.
  // Both modes render a `Switch`, but the multi-mode parent toggle
  // gets an inline "Mixed" caption when the selection is mixed and
  // un-touched, so the column width still varies row-to-row.
  const { ref: toggleColumnRef, leftPx: railLeftPx } = useToggleColumnCenter(hasChildren);
  // Per-flag pending state is no longer surfaced on the row itself
  // — the bulk drawer's sticky footer carries the aggregate count,
  // and the Review-changes confirmation dialog lists every staged
  // flag with its before/after chips. Users revert via the Switch
  // (homogeneous: toggle back to original; mixed: re-pick a value)
  // or by closing the drawer.
  const enabledChildKeys = useMemo(
    () =>
      hasChildren
        ? node.children
            .map((c) => c.key)
            .filter((k) => isFlagEffectivelyOn(k, panel.currentStates, panel.pendingChanges))
        : [],
    [hasChildren, node.children, panel.currentStates, panel.pendingChanges]
  );

  // Clicking anywhere on the label/description area (but not on the
  // toggle itself, which is a sibling) expands or collapses the section.
  // Sub-flag rows (`hasChildren === false`) have no expand action, so we
  // only attach the handler when it'd actually do something.
  const labelBoxClickProps = hasChildren
    ? {
        onClick: toggleExpanded,
        sx: {
          cursor: "pointer",
          userSelect: "none" as const,
        },
        role: "button" as const,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpanded();
          }
        },
      }
    : {};

  const rowContent = (
    <>
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* Wrap FlagControl so we can measure its rendered width and
            position the children rail under its horizontal center.
            `inline-flex` keeps the wrapper sized to the control. */}
        <Box ref={toggleColumnRef} sx={{ display: "inline-flex" }}>
          <FlagControl flagKey={node.key} depth={depth} {...panel} />
        </Box>
        <Box flex={1} minWidth={0} {...labelBoxClickProps}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="body1" fontWeight={depth === 0 ? 600 : 400}>
              {meta.label}
            </Typography>
            <FlagChips meta={meta} />
            {hasChildren && !expanded && <CollapsedAddOnsBadge enabledKeys={enabledChildKeys} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block">
            {meta.description}
          </Typography>
          <DependencyHint flagKey={node.key} currentStates={panel.currentStates} />
        </Box>
        {hasChildren && (
          <IconButton
            size="small"
            aria-label={
              expanded ? `Collapse ${meta.label} sub-flags` : `Expand ${meta.label} sub-flags`
            }
            aria-expanded={expanded}
            onClick={toggleExpanded}
          >
            <ChevronRightIcon
              fontSize="small"
              sx={{
                transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 200ms ease-in-out",
              }}
            />
          </IconButton>
        )}
      </Stack>
      {hasChildren && (
        <Collapse in={expanded} unmountOnExit>
          {/* Single continuous spine behind the entire children stack.
              Because the spine is drawn by the children container and
              not by each row, the 12px Stack gaps don't break the
              line. Spine span is the full height of this Box, which
              includes those gaps. */}
          <Box
            sx={{
              position: "relative",
              mt: 1,
              "&::before": {
                content: '""',
                position: "absolute",
                left: `${railLeftPx}px`,
                top: 0,
                bottom: 0,
                width: `${RAIL_WIDTH_PX}px`,
                backgroundColor: (theme) => theme.palette.divider,
              },
            }}
          >
            <Stack spacing={1.5}>
              {node.children.map((child) => (
                <FlagRow key={child.key} node={child} depth={depth + 1} {...panel} />
              ))}
            </Stack>
          </Box>
        </Collapse>
      )}
    </>
  );

  if (depth === 0) {
    return <Box>{rowContent}</Box>;
  }

  // Indent sub-flag content to line up with the parent's content (58px
  // for the toggle column + 16px gap, matching the depth-0 Stack's
  // `spacing={2}`). The spine is drawn by our parent's children
  // container, so no per-row rail is needed here.
  return <Box sx={{ pl: `${PARENT_TOGGLE_WIDTH_PX + 16}px` }}>{rowContent}</Box>;
}

// MUI's `size="small"` chip renders at 24px tall with 13px text, which is
// a bit heavy when stacked inline next to flag labels. Shrink both while
// keeping the outlined silhouette so they still read as categorical tags.
//
// Subtlety: the font size MUST be set on the `.MuiChip-label` selector
// rather than the chip root — MUI applies its own `fontSize` to the
// label via the `size` variant, which would otherwise win over a root
// sx declaration. With the correct font size, `lineHeight: 1` lets the
// flex-centered label track the chip's 20px height exactly.
const COMPACT_CHIP_SX = {
  height: 20,
  "& .MuiChip-label": {
    fontSize: "0.6875rem", // 11px
    lineHeight: 1,
    px: 0.75,
  },
} as const;

// Tooltip-only indicators (staff-only icon, "in development" / "default
// on" chips, and the `+N` add-ons badge) are not interactive — they
// surface info on hover. Use `cursor: "help"` consistently across all of
// them so hover behavior reads as "this is informational" rather than
// inviting a click. Mirrors the existing cursor on the `+N` badge.
const INFORMATIONAL_INDICATOR_SX = {
  width: 18,
  height: 18,
  display: "block",
  cursor: "help",
} as const;

const COMPACT_CHIP_INFORMATIONAL_SX = {
  ...COMPACT_CHIP_SX,
  cursor: "help",
} as const;

/**
 * Resolve a flag's effective on/off state with pending changes layered on
 * top of `currentStates`. Multi-tenant "mixed" is treated as not-strictly-
 * on (matching how dependency hints and the `+N` add-on count behave —
 * both want a conservative "definitely enabled" answer).
 */
export function isFlagEffectivelyOn(
  key: FeatureFlagKey,
  currentStates: Partial<Record<FeatureFlagKey, FlagState>>,
  pendingChanges: Partial<Record<FeatureFlagKey, PendingValue>> | undefined
): boolean {
  const pending = pendingChanges?.[key];
  if (pending !== undefined) return pending === true;
  return currentStates[key] === true;
}

/**
 * Tooltip body shared between the staff list page chip and the toggle
 * panel's collapsed-module badge — both surfaces need to read identically
 * so users learn the format once. When `enabledKeys` is empty we fall
 * back to the caller-supplied empty text so we don't show a header with
 * nothing under it.
 */
export function EnabledFlagsTooltipBody({
  enabledKeys,
  header,
  emptyText,
  lead,
}: {
  enabledKeys: readonly FeatureFlagKey[];
  header: string;
  emptyText: string;
  /**
   * Optional headline rendered above `header`, separated by a small
   * vertical gap. Used by module chips on the staff feature-flag grid
   * to clarify that the module itself is on (e.g. "Waste enabled")
   * before listing the enabled add-ons under it.
   */
  lead?: string;
}) {
  const { getMeta } = useFeatureFlagMeta();
  if (enabledKeys.length === 0) return <>{emptyText}</>;
  return (
    <Box>
      {lead && (
        <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1 }}>
          {lead}
        </Typography>
      )}
      <Typography variant="caption" fontWeight={600} display="block">
        {header}
      </Typography>
      {enabledKeys.map((k) => (
        <Typography key={k} variant="caption" display="block">
          {getMeta(k).label}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * "+N" affordance shown beside a module's chips when its accordion is
 * collapsed, so staff can see at a glance how many add-ons are enabled
 * without expanding the section. Hovering the badge reveals the same
 * tooltip the list-page chip shows.
 *
 * Renders nothing when no add-ons are enabled — a "+0" badge would just
 * add visual noise to a row that's already telling the user "module on,
 * nothing else". Mirrors the list-page chip behavior.
 */
function CollapsedAddOnsBadge({ enabledKeys }: { enabledKeys: FeatureFlagKey[] }) {
  if (enabledKeys.length === 0) return null;
  return (
    <Tooltip
      title={
        <EnabledFlagsTooltipBody
          enabledKeys={enabledKeys}
          header="Add-ons enabled:"
          emptyText="No add-ons enabled"
        />
      }
      arrow
    >
      <Typography
        component="span"
        variant="caption"
        sx={{
          color: "encampBlue.main",
          fontWeight: 600,
          cursor: "help",
          lineHeight: 1,
        }}
      >
        +{enabledKeys.length}
      </Typography>
    </Tooltip>
  );
}

function FlagChips({ meta }: { meta: FeatureFlagMetadata }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" useFlexGap>
      {meta.staffOverride && (
        <Tooltip title="This feature is always visible for Staff users regardless of the feature flag">
          {/* Icon-only affordance using the Encamp logo so the staff-only
              indicator is visually consistent with the brand mark in the
              left nav. The tooltip carries the full explanation. */}
          <Box
            component="img"
            src={EncampLogo}
            alt="Always visible for staff"
            aria-label="Always visible for staff"
            sx={INFORMATIONAL_INDICATOR_SX}
          />
        </Tooltip>
      )}
      {meta.inDevelopment && (
        <Tooltip title="This flag is still under active development.">
          <Chip
            size="small"
            label="in development"
            color="info"
            variant="outlined"
            sx={COMPACT_CHIP_INFORMATIONAL_SX}
          />
        </Tooltip>
      )}
      {meta.defaultValue && (
        <Tooltip title="Enabled by default for tenants without an explicit setting.">
          <Chip
            size="small"
            label="default on"
            color="success"
            variant="outlined"
            sx={COMPACT_CHIP_INFORMATIONAL_SX}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

function DependencyHint({
  flagKey,
  currentStates,
}: {
  flagKey: FeatureFlagKey;
  currentStates: Partial<Record<FeatureFlagKey, FlagState>>;
}) {
  const { getMeta } = useFeatureFlagMeta();
  const meta = getMeta(flagKey);

  const labels = (keys: readonly FeatureFlagKey[]) => keys.map((k) => getMeta(k).label).join(", ");

  const thisFlagOn = currentStates[flagKey] === true || currentStates[flagKey] === "mixed";

  if (thisFlagOn) {
    // Both the implicit parent dependency and explicit `requires` need
    // to be on for this flag to be functional for the customer. Treat
    // them uniformly here so a child flag whose parent is off renders
    // the same kind of warning as an unmet `requires`.
    const allDeps: FeatureFlagKey[] = [
      ...(meta.parentKey ? [meta.parentKey] : []),
      ...meta.requires,
    ];
    if (allDeps.length === 0) return null;
    // In multi mode `currentStates[k]` may be "mixed"; treat anything that
    // isn't strictly `true` as "not guaranteed on" so we don't falsely
    // claim the dependency is satisfied across a mixed selection.
    const unmet = allDeps.filter((k) => currentStates[k] !== true);
    if (unmet.length === 0) return null;

    // A `staffOverride: true` dep is on for staff regardless of the
    // stored customer value, so for staff it's effectively satisfied.
    // Partition unmet deps so we can show "active for staff only" when
    // every unmet dep is staff-overridden, vs. the stronger "inactive"
    // when at least one dep is genuinely off.
    const trulyOff = unmet.filter((k) => !getMeta(k).staffOverride);
    const staffOnlyMissing = unmet.filter((k) => getMeta(k).staffOverride);

    if (trulyOff.length > 0) {
      return (
        <Box mt={0.5}>
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`Inactive — needs ${labels(unmet)}`}
            sx={COMPACT_CHIP_SX}
          />
        </Box>
      );
    }

    // Staff orange (MUI `warning`) signals "this is a staff-only
    // affordance" consistently with the rest of the staff UI (left
    // nav, staff override icon, etc.). Same hue as the "Inactive"
    // chip above — the labels are different enough that the shared
    // color works as a "this row needs your attention" signal.
    return (
      <Box mt={0.5}>
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={`Active for staff only — customer needs ${labels(staffOnlyMissing)}`}
          sx={COMPACT_CHIP_SX}
        />
      </Box>
    );
  }

  // Flag is off. Show informational "Requires: …" only for explicit
  // `requires` — the parent relationship is already conveyed by the
  // indented sub-flag tree, so repeating it as a caption would just be
  // noise on every add-on row.
  if (meta.requires.length === 0) return null;
  return (
    <Typography variant="caption" display="block" sx={{ mt: 0.25, color: "info.dark" }}>
      Requires: {labels(meta.requires)}
    </Typography>
  );
}

type FlagControlProps = FeatureFlagTogglePanelProps & {
  flagKey: FeatureFlagKey;
  depth: number;
};

// Indeterminate state borrows the convention from Figma's "Switch
// (intermediate)" pattern: hide the thumb and paint a small white
// dash centered in a neutral grey track. Sized per Switch size so
// the dash visually balances against the track width.
const INDETERMINATE_DASH_WIDTH_PX: Record<"medium" | "small", number> = {
  medium: 10,
  small: 8,
};
const INDETERMINATE_DASH_HEIGHT_PX = 2;

function FlagControl({
  mode,
  currentStates,
  pendingChanges,
  onChange,
  disabled,
  flagKey,
  depth,
}: FlagControlProps) {
  const current = currentStates[flagKey] ?? false;
  const pending = pendingChanges?.[flagKey];
  const effective: FlagState = pending === undefined ? current : pending;
  const controlSize: "medium" | "small" = depth > 0 ? "small" : "medium";

  if (mode === "single") {
    return (
      <Switch
        size={controlSize}
        checked={effective === true}
        disabled={disabled}
        onChange={(_e, checked) => onChange(flagKey, checked)}
        inputProps={{ "aria-label": `Toggle ${flagKey}` }}
      />
    );
  }

  // ─── Multi mode ─────────────────────────────────────────────────
  const isMixedOriginal = current === "mixed";
  const isMixedPristine = isMixedOriginal && pending === undefined;
  const checked = effective === true;

  const handleChange = (_e: unknown, nextChecked: boolean) => {
    // Clicking back to the homogeneous original auto-clears the
    // pending entry so users get a fast revert path. Mixed origins
    // have no boolean "original", so any click on the indeterminate
    // switch is treated as a commit — there's no auto-clear path,
    // and the only way back to indeterminate is to close the bulk
    // drawer with Cancel.
    if (!isMixedOriginal && nextChecked === current) {
      onChange(flagKey, undefined);
      return;
    }
    onChange(flagKey, nextChecked);
  };

  const dashWidthPx = INDETERMINATE_DASH_WIDTH_PX[controlSize];
  // Theme-aware sx for the indeterminate visual: hide the thumb
  // and draw a centered white dash on a neutral grey track. Cleared
  // as soon as a pending value exists so the Switch returns to its
  // native thumb-on-track presentation. Keeping the SwitchBase (and
  // its input) in place means clicks are still received normally —
  // only the visible thumb is suppressed.
  const sxOverride: NonNullable<React.ComponentProps<typeof Switch>["sx"]> = isMixedPristine
    ? (theme) => ({
        "& .MuiSwitch-thumb": {
          display: "none",
        },
        "& .MuiSwitch-track": {
          opacity: 1,
          backgroundColor: theme.palette.grey[500],
          backgroundImage: `linear-gradient(${theme.palette.common.white}, ${theme.palette.common.white})`,
          backgroundSize: `${dashWidthPx}px ${INDETERMINATE_DASH_HEIGHT_PX}px`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        },
      })
    : {};

  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    // Direction-of-click commit only matters in the indeterminate
    // state — once a pending value exists the Switch is a normal
    // binary toggle. Intercept on capture so we run before MUI's
    // SwitchBase change handler, and `preventDefault` on the click
    // suppresses the native checkbox toggle so our explicit value
    // wins instead of MUI's "flip from current" behavior.
    if (!isMixedPristine) return;
    // Keyboard activations (focus + Space/Enter) synthesize a click
    // with `clientX === 0` and `detail === 0`. Our x-position
    // heuristic would always commit OFF for those, stranding
    // keyboard-only users with no way to reach ON from the
    // indeterminate state. Bail out and let MUI's native Switch
    // flip handle the activation instead — the SwitchBase change
    // handler then routes through `handleChange` like any other
    // commit.
    if (e.detail === 0) return;
    // Measure against the Switch root, not the wrapping inline-flex
    // Box, so the click region matches the visible glyph rather
    // than any padding the Box may pick up.
    const switchRoot = e.currentTarget.querySelector(".MuiSwitch-root");
    if (!(switchRoot instanceof HTMLElement)) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = switchRoot.getBoundingClientRect();
    const midpoint = rect.left + rect.width / 2;
    const nextValue = e.clientX >= midpoint;
    onChange(flagKey, nextValue);
  };

  const switchEl = (
    <Switch
      size={controlSize}
      checked={checked}
      disabled={disabled}
      onChange={handleChange}
      sx={sxOverride}
      inputProps={{ "aria-label": `Toggle ${flagKey}` }}
    />
  );

  if (!isMixedPristine) {
    // No tri-state hint needed when the row already has a definite
    // value (homogeneous or staged); the Switch position itself is
    // self-explanatory.
    return (
      <Box onClickCapture={handleClickCapture} sx={{ display: "inline-flex" }}>
        {switchEl}
      </Box>
    );
  }

  return (
    <Box onClickCapture={handleClickCapture} sx={{ display: "inline-flex" }}>
      <Tooltip
        title="There is a mix of ON and OFF values for this flag. Leave alone if you want each tenant to keep their current setting."
        arrow
      >
        {/* MUI Tooltip needs a single ref-forwarding child, and
            wrapping in a span keeps the hover target snug to the
            Switch without absorbing pointer events outside it. */}
        <span style={{ display: "inline-flex" }}>{switchEl}</span>
      </Tooltip>
    </Box>
  );
}

/**
 * Helper so callers can render a consistent "N pending changes"
 * summary (and gate the Apply button) without re-implementing the
 * counting logic. The bulk drawer surfaces this string inline in
 * its sticky footer.
 */
export function countPendingChanges(
  pendingChanges: Partial<Record<FeatureFlagKey, PendingValue>>
): number {
  return Object.values(pendingChanges).filter((v) => v !== undefined).length;
}
